import { ComponentIds, HierarchyUtils, ProductUtils, ValidationResultFail, ValidationResultType } from '@inter-ikea-core/core';
import { ValidationBase } from '@inter-ikea-core/core-toolbox';
import { AdjustableComponent, AdjustableHostComponent, AdjustableStateComponent, CLOTH_RAIL_ALIGN_SERIES_LEFT, CLOTH_RAIL_INSIDE_CONNECTOR_KEY, CLOTH_RAIL_OUTSIDE_CONNECTOR_KEY, hasCorrectDistanceToNextFrame, measureClothRailToTheRight } from '@inter-ikea-storageone/range';
/**
 * Validation when dragging a cloth rail.
 */
export class AdjustableClothRailValidation extends ValidationBase {
    /**
     * Constructor for AdjustableClothRailValidation.
     * @param coreApi - The core API instance.
     */
    constructor(coreApi) {
        super();
        this.name = 'AdjustableClothRailValidation';
        this.id = 'ADJUSTABLE_CLOTH_RAIL_VALIDATION';
        this.isClothRailConnectedToFrame = (entity, snapActionCandidate) => {
            return (entity.hasComponent(AdjustableComponent.id) &&
                snapActionCandidate.host.hasComponent(ComponentIds.FRAME_COMPONENT_ID));
        };
        this.validateClothRailPosition = (clothRailEntity, snapActionCandidate) => {
            // If the cloth rail snaps inside a frame we allow it and do not need to calculate all the stuff for outside placement
            if (snapActionCandidate.hostConnector.key.includes('adjustable-cloth-rail-inside-left')) {
                return this.validateInsideFramePlacement(clothRailEntity, snapActionCandidate);
            }
            return this.validateClothRailPositionToTheRight(clothRailEntity, snapActionCandidate);
        };
        this.validateClothRailPositionToTheRight = (clothRailEntity, snapActionCandidate) => {
            const outerHit = measureClothRailToTheRight(this.coreApi, clothRailEntity);
            const subConnectorHole = this.getHostConnectorNumber(snapActionCandidate);
            if (!outerHit) {
                return new ValidationResultFail('Invalid distance for product', ProductUtils.productRefOrEmptyString(clothRailEntity));
            }
            if (outerHit) {
                if (outerHit.entity.hasComponent(AdjustableHostComponent.id)) {
                    if (hasCorrectDistanceToNextFrame(outerHit.distance, clothRailEntity)) {
                        const rightConnectorIsTaken = this.isSubConnectorTaken(outerHit.entity, CLOTH_RAIL_INSIDE_CONNECTOR_KEY + subConnectorHole, clothRailEntity.id);
                        if (rightConnectorIsTaken) {
                            return new ValidationResultFail('Host connector is already taken', ProductUtils.productRefOrEmptyString(clothRailEntity));
                        }
                        const verticallyAlignedResult = this.validateHostAndFrameToTheRightAreVerticallyAligned(snapActionCandidate.host, outerHit.entity);
                        if (verticallyAlignedResult?.resultType === ValidationResultType.FAIL) {
                            return verticallyAlignedResult;
                        }
                    }
                    else {
                        return new ValidationResultFail('Invalid distance for product', ProductUtils.productRefOrEmptyString(clothRailEntity));
                    }
                }
                else {
                    return new ValidationResultFail('Entity does not have required component AdjustableHostComponent', ProductUtils.productRefOrEmptyString(clothRailEntity));
                }
            }
            // Check that the cloth rails left connector is not taken by another cloth rail
            if (this.isSubConnectorTaken(snapActionCandidate.host, CLOTH_RAIL_INSIDE_CONNECTOR_KEY + subConnectorHole, clothRailEntity.id))
                return new ValidationResultFail('Placement is already taken', ProductUtils.productRefOrEmptyString(clothRailEntity));
            return null;
        };
        this.validateClothRailPositionToTheLeft = (clothRailEntity, snapActionCandidate) => {
            const outerHit = measureClothRailToTheRight(this.coreApi, clothRailEntity);
            if (!outerHit) {
                return new ValidationResultFail('Invalid distance for product', ProductUtils.productRefOrEmptyString(clothRailEntity));
            }
            if (outerHit) {
                if (outerHit.entity.hasComponent(AdjustableHostComponent.id)) {
                    if (hasCorrectDistanceToNextFrame(outerHit.distance, clothRailEntity)) {
                        const subConnectorHole = this.getHostConnectorNumber(snapActionCandidate);
                        const rightConnectorIsTaken = this.isSubConnectorTaken(outerHit.entity, CLOTH_RAIL_INSIDE_CONNECTOR_KEY + subConnectorHole, clothRailEntity.id);
                        if (rightConnectorIsTaken) {
                            return new ValidationResultFail('Invalid distance for product', ProductUtils.productRefOrEmptyString(clothRailEntity));
                        }
                    }
                    else {
                        return new ValidationResultFail('Invalid distance for product', ProductUtils.productRefOrEmptyString(clothRailEntity));
                    }
                }
            }
            return null;
        };
        this.validateInsideFramePlacement = (clothRailEntity, snapActionCandidate) => {
            const subConnectorHole = this.getHostConnectorNumber(snapActionCandidate);
            const clothRailsOnFrameRightSide = snapActionCandidate.host
                ?.getComponent(ComponentIds.HOST_CONNECTIONS_COMPONENT_ID)
                ?.connections.filter(c => c.hostConnector.key.includes(CLOTH_RAIL_OUTSIDE_CONNECTOR_KEY))
                .map(c => {
                return c.hostConnector.key.split('-').pop();
            });
            const snapActionCandidateAlignmentConnections = snapActionCandidate.host
                .getComponent(ComponentIds.ALIGNMENT_CONNECTIONS_COMPONENT_ID)
                ?.connections.filter(c => c.offeringEntity.hasComponent(AdjustableStateComponent.id));
            const clothRailsOnTheLeftSide = snapActionCandidateAlignmentConnections
                ?.filter(c => c.demandingConnector.key.includes(CLOTH_RAIL_ALIGN_SERIES_LEFT))
                .map(c => {
                return c.demandingConnector.key.split('-').pop();
            });
            if (clothRailsOnFrameRightSide?.includes(subConnectorHole) ||
                clothRailsOnTheLeftSide?.includes(subConnectorHole)) {
                return new ValidationResultFail('Invalid placement. The connector is already taken', ProductUtils.productRefOrEmptyString(clothRailEntity));
            }
            return null;
        };
        this.getHostConnectorNumber = (snapActionCandidate) => {
            return snapActionCandidate.hostConnector
                .closestSubConnector(snapActionCandidate.hostConnector.transform.position)
                ?.key.split('-')
                .pop();
        };
        this.isSubConnectorTaken = (frame, connectorName, clothRailId) => {
            const frameClothRails = HierarchyUtils.children(frame).filter(e => e.hasComponent(AdjustableComponent.id) && e.id !== clothRailId);
            return frameClothRails.some(clothRail => clothRail.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)?.connections[0].hostConnector.key === connectorName);
        };
        /**
         * Validate that the host frame and the frame to the right of it are vertically aligned.
         * Frames that are not aligned vertically will not be able to connect to each other.
         * @param hostFrame Frame that is the host for the cloth rail
         * @param frameToTheRight Frame that is to the right of the host frame
         * @returns ValidationResult if the frames are not aligned vertically, null if they are aligned
         */
        this.validateHostAndFrameToTheRightAreVerticallyAligned = (hostFrame, frameToTheRight) => {
            const outerHitFrameSize = frameToTheRight.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
            const outerHitFramePosition = frameToTheRight.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform.position;
            const parentFrameSize = hostFrame.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
            const parentFramePosition = hostFrame.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform.position;
            if (outerHitFrameSize && outerHitFramePosition && parentFrameSize && parentFramePosition) {
                const frameToTheRightBottom = Math.round(outerHitFramePosition.Y - outerHitFrameSize.height / 2);
                const hostFrameBottom = Math.round(parentFramePosition.Y - parentFrameSize.height / 2);
                if (frameToTheRightBottom !== hostFrameBottom) {
                    return new ValidationResultFail('Host and frame to the right are not aligned vertically', ProductUtils.productRefOrEmptyString(hostFrame));
                }
            }
            return null;
        };
        this.coreApi = coreApi;
    }
    validateCurrentState() {
        return null;
    }
    validateSnap(snapActionCandidate) {
        if (!this.isEnabled)
            return null;
        const entity = snapActionCandidate.guestRoot;
        // Validation of a cloth rail attached to a frame
        if (this.isClothRailConnectedToFrame(entity, snapActionCandidate)) {
            return this.validateClothRailPosition(entity, snapActionCandidate);
        }
        return null;
    }
}
//# sourceMappingURL=AdjustableClothRailValidation.js.map